import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface RegionData {
  roId: number;
  roName: string;
  roCode: string;
  totalSanctioned: number;
  totalReceived: number;
  totalSpent: number;
  overallUtilization: number;
}

interface RegionPerformanceBarChartProps {
  data: RegionData[];
  height?: number;
  onClick?: (region: RegionData) => void;
}

const RegionPerformanceBarChart: React.FC<RegionPerformanceBarChartProps> = ({
  data,
  height = 400,
  onClick,
}) => {
  const formatCurrency = (value: number) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    }
    return `₹${value.toLocaleString()}`;
  };

  const getBarColor = (utilization: number) => {
    if (utilization >= 75) return '#10b981'; // green-500
    if (utilization >= 50) return '#f59e0b'; // yellow-500
    if (utilization >= 25) return '#f97316'; // orange-500
    return '#ef4444'; // red-500
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const region = payload[0].payload;
      return (
        <div className="bg-white shadow-lg rounded-lg p-4 border border-gray-200">
          <p className="font-semibold text-gray-900 mb-2">{region.roName}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Sanctioned:</span>
              <span className="font-medium text-gray-900">{formatCurrency(region.totalSanctioned)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Received:</span>
              <span className="font-medium text-purple-600">{formatCurrency(region.totalReceived)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Spent:</span>
              <span className="font-medium text-green-600">{formatCurrency(region.totalSpent)}</span>
            </div>
            <div className="flex justify-between gap-4 pt-2 border-t border-gray-200 mt-2">
              <span className="text-gray-600">Utilization:</span>
              <span className={`font-bold ${region.overallUtilization >= 75 ? 'text-green-600' :
                region.overallUtilization >= 50 ? 'text-yellow-600' :
                  region.overallUtilization >= 25 ? 'text-orange-600' : 'text-red-600'
                }`}>
                {region.overallUtilization.toFixed(1)}%
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 italic">Click to view details</p>
        </div>
      );
    }
    return null;
  };

  const handleBarClick = (data: any) => {
    if (onClick && data) {
      // Extract the region data from the clicked bar
      // In Recharts Bar onClick, data is the actual data point
      if (data && data.roId) {
        onClick(data);
      }
    }
  };

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="roName"
            angle={-45}
            textAnchor="end"
            height={100}
            interval={0}
            tick={{ fontSize: 11, dx: -10, dy: 25 }}
          />
          <YAxis
            tickFormatter={formatCurrency}
            width={80}
            label={{ value: 'Amount', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            height={36}
            wrapperStyle={{ paddingBottom: '20px' }}
          />
          <Bar
            dataKey="totalSanctioned"
            name="Sanctioned"
            fill="#3b82f6"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={handleBarClick}
          />
          <Bar
            dataKey="totalReceived"
            name="Received"
            fill="#a855f7"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={handleBarClick}
          />
          <Bar
            dataKey="totalSpent"
            name="Spent"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={handleBarClick}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.overallUtilization)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RegionPerformanceBarChart;
